/**
 * Consult API Client
 * 상담 관련 API 호출
 */

import apiClient from "./client";
import type {
  Consult,
  ConsultSearchParams,
  PagedConsults,
  ConsultType,
} from "@/types/consult";
import { PAGINATION } from "@/constants/pagination";
import { SORT_ORDER } from "@/constants/sort";

/**
 * V1056: 신규 상담 등록 요청 타입 (#13)
 */
export interface ConsultCreateRequest {
  customerName: string;
  customerPhone: string;
  type: ConsultType;
  handlerId?: number;
  memo?: string;
}

export const consultApi = {
  /**
   * 상담 목록 조회 (검색/필터/페이징)
   * GET /api/consults
   */
  getConsults: async (params: ConsultSearchParams): Promise<PagedConsults> => {
    const queryParams = new URLSearchParams();

    if (params.type && params.type !== "ALL") {
      queryParams.append("type", params.type);
    }
    if (params.result && params.result !== "ALL") {
      queryParams.append("result", params.result);
    }
    if (params.visited !== undefined) {
      queryParams.append("visited", params.visited.toString());
    }
    if (params.q) {
      queryParams.append("q", params.q);
    }
    if (params.customerName) {
      queryParams.append("customerName", params.customerName);
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach((s) => queryParams.append("status", s));
    }
    // V149: 긴급도 필터
    if (params.priority && params.priority !== "ALL") {
      queryParams.append("priority", params.priority);
    }
    // V152: 콜백 상태 필터
    if (params.callbackStatus) {
      queryParams.append("callbackStatus", params.callbackStatus);
    }
    if (params.from) {
      queryParams.append("from", params.from);
    }
    if (params.to) {
      queryParams.append("to", params.to);
    }
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append("size", params.size.toString());
    }
    if (params.sort) {
      queryParams.append("sort", params.sort);
    }
    if (params.handlerId) {
      queryParams.append("handlerId", params.handlerId.toString());
    }
    // V1243 (Stage 3): 미확인 상담만 보기
    if (params.unacknowledgedOnly) {
      queryParams.append("unacknowledgedOnly", "true");
    }
    // Phase 0D: appointmentStatus 필터와 dateField는 Appointment 기반으로 이동됨

    const response = await apiClient.get<PagedConsults>(
      `/api/consults?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * V166: 검색 조건과 동일한 필터로 요약 카운트 조회
   * GET /api/consults/summary-counts
   * 상담이력 상단 카드에 사용 (콜백 대기/예약 대기/긴급/오늘 처리)
   */
  getSummaryCounts: async (
    params: ConsultSearchParams,
  ): Promise<Record<string, number>> => {
    const queryParams = new URLSearchParams();

    if (params.type && params.type !== "ALL")
      queryParams.append("type", params.type);
    if (params.result && params.result !== "ALL")
      queryParams.append("result", params.result);
    if (params.q) queryParams.append("q", params.q);
    if (params.customerName)
      queryParams.append("customerName", params.customerName);
    if (params.status && params.status.length > 0) {
      params.status.forEach((s) => queryParams.append("status", s));
    }
    if (params.priority && params.priority !== "ALL")
      queryParams.append("priority", params.priority);
    if (params.callbackStatus)
      queryParams.append("callbackStatus", params.callbackStatus);
    if (params.from) queryParams.append("from", params.from);
    if (params.to) queryParams.append("to", params.to);
    if (params.handlerId)
      queryParams.append("handlerId", params.handlerId.toString());
    // V1243 (Stage 3): 미확인 상담만 보기
    if (params.unacknowledgedOnly)
      queryParams.append("unacknowledgedOnly", "true");

    const response = await apiClient.get<Record<string, number>>(
      `/api/consults/summary-counts?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * 상담 상세 조회 (POWER/ADMIN 전용)
   * GET /api/consults/{id}
   */
  getConsultDetail: async (id: number): Promise<Consult> => {
    const response = await apiClient.get<Consult>(`/api/consults/${id}`);
    return response.data;
  },

  /**
   * 녹음 Signed URL 재발급
   * GET /api/consults/{id}/recording-url
   * 상세 응답의 recordingUrl이 null이거나 만료된 경우 재생 직전 호출.
   */
  getRecordingUrl: async (id: number): Promise<string> => {
    const response = await apiClient.get<{
      success: boolean;
      signedUrl?: string;
      code?: string;
      message?: string;
    }>(`/api/consults/${id}/recording-url`);
    if (!response.data.success || !response.data.signedUrl) {
      throw new Error(response.data.message || "녹음 URL을 불러올 수 없습니다");
    }
    return response.data.signedUrl;
  },

  /**
   * 상담 상태 변경
   * PUT /api/consults/{id}/status
   * Phase 15: selectedPriority 파라미터 추가
   */
  updateStatus: async (
    id: number,
    status: string,
    handler?: string,
    note?: string,
    selectedPriority?: number,
  ): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/status`,
      {
        status,
        handler,
        note,
        selectedPriority,
      },
    );
    return response.data;
  },

  /**
   * 담당자 변경
   * PUT /api/consults/{id}/assignee
   */
  updateAssignee: async (id: number, assignee: string): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/assignee`,
      {
        assignee,
      },
    );
    return response.data;
  },

  /**
   * V168: 상담자 일괄 배분
   * PUT /api/consults/bulk-assign
   */
  bulkAssign: async (
    ids: number[],
    assignee: string,
  ): Promise<{ updatedCount: number }> => {
    const response = await apiClient.put<{ updatedCount: number }>(
      `/api/consults/bulk-assign`,
      { ids, assignee },
    );
    return response.data;
  },

  /**
   * 메모 업데이트
   * PUT /api/consults/{id}/notes
   */
  updateNotes: async (
    id: number,
    adminNotes?: string,
    specialNotes?: string,
  ): Promise<Consult> => {
    const response = await apiClient.put<Consult>(`/api/consults/${id}/notes`, {
      adminNotes,
      specialNotes,
    });
    return response.data;
  },

  /**
   * 아웃바운드 대기 목록 조회 (POWER 이상)
   * GET /api/consults/outbound-queue
   */
  getOutboundQueue: async (
    page = 0,
    size = PAGINATION.DEFAULT_SIZE,
  ): Promise<PagedConsults> => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await apiClient.get<PagedConsults>(
      `/api/consults/outbound-queue?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * 아웃바운드 필요 표시
   * PUT /api/consults/{id}/outbound/required
   */
  markOutboundRequired: async (
    id: number,
    reason: string,
  ): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/outbound/required`,
      { reason },
    );
    return response.data;
  },

  /**
   * 아웃바운드 완료 처리
   * PUT /api/consults/{id}/outbound/complete
   */
  completeOutbound: async (id: number, result: string): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/outbound/complete`,
      { result },
    );
    return response.data;
  },

  /**
   * Phase P0-3: 아웃바운드 승인
   * PUT /api/consults/{id}/outbound/approve
   *
   * 관리자가 AI 아웃바운드 요청을 승인하고 응답 내용을 작성합니다.
   *
   * @param id 상담 ID
   * @param responseContent 관리자가 작성한 AI 전화 응답 내용 (필수)
   * @param scheduledAt AI 전화 예약 시간 (선택, NULL이면 즉시 실행)
   * @param maxAttempts 최대 재시도 횟수 (선택, 기본값 3)
   */
  approveOutbound: async (
    id: number,
    responseContent: string,
    scheduledAt?: string,
    maxAttempts?: number,
  ): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/outbound/approve`,
      {
        responseContent,
        scheduledAt,
        maxAttempts,
      },
    );
    return response.data;
  },

  /**
   * Phase P0-3: 아웃바운드 거부
   * PUT /api/consults/{id}/outbound/reject
   *
   * 관리자가 AI 아웃바운드 요청을 거부하고 콜백으로 전환합니다.
   *
   * @param id 상담 ID
   * @param reason 거부 사유 (필수)
   */
  rejectOutbound: async (id: number, reason: string): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/outbound/reject`,
      { reason },
    );
    return response.data;
  },

  /**
   * 실방문 상태 토글
   * PATCH /api/consults/{id}/visited
   */
  toggleVisited: async (id: number, visited: boolean): Promise<Consult> => {
    const response = await apiClient.patch<Consult>(
      `/api/consults/${id}/visited`,
      {
        visited,
      },
    );
    return response.data;
  },

  /**
   * V1243 (Stage 3): 명시적 "확인" — 사용자가 검토 완료 표시
   * PUT /api/consults/{id}/acknowledge
   */
  acknowledge: async (id: number): Promise<Consult> => {
    const response = await apiClient.put<Consult>(
      `/api/consults/${id}/acknowledge`,
    );
    return response.data;
  },

  /**
   * V1243 (Stage 3): "확인" 취소
   * DELETE /api/consults/{id}/acknowledge
   */
  unacknowledge: async (id: number): Promise<Consult> => {
    const response = await apiClient.delete<Consult>(
      `/api/consults/${id}/acknowledge`,
    );
    return response.data;
  },

  /**
   * 고객별 상담 이력 조회
   * GET /api/consults/by-customer/{customerId}
   * Customer Management Page에서 사용
   */
  getConsultsByCustomer: async (
    customerId: number,
    page = 0,
    size = PAGINATION.SMALL_SIZE,
  ): Promise<PagedConsults> => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort: SORT_ORDER.CONSULT,
    });

    const response = await apiClient.get<PagedConsults>(
      `/api/consults/by-customer/${customerId}?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * AI 재분석 요청
   * POST /api/consults/{id}/reanalyze
   *
   * 분석 실패(FAILED) 상태의 상담을 재분석합니다.
   * force=true로 호출하면 완료된 분석도 재분석합니다.
   *
   * @param id 상담 ID
   * @param force 완료된 분석도 재분석할지 여부 (기본값: true)
   */
  reanalyze: async (
    id: number,
    force: boolean = true,
  ): Promise<{
    success: boolean;
    message: string;
    consultId: number;
    conversationId?: number;
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      consultId: number;
      conversationId?: number;
    }>(`/api/consults/${id}/reanalyze?force=${force}`);
    return response.data;
  },

  /**
   * 신규 상담 등록
   * POST /api/consults
   */
  create: async (data: ConsultCreateRequest): Promise<Consult> => {
    const response = await apiClient.post<Consult>("/api/consults", data);
    return response.data;
  },
};
